import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMerchantOverview, listPayments, type MerchantOverview, type Payment } from "@/lib/api";
import { formatAmount, formatRelative, formatVolumes } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { Badge, statusColor } from "@/components/DataTable";
import { TrendingUp, CheckCircle2, Activity, Wallet, ArrowRight } from "lucide-react";
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const METHOD_COLORS: Record<string, string> = {
  mpesa: "#00A551",
  orange: "#FF7A00",
  airtel: "#E4002B",
  afrimoney: "#F5B300",
  visa: "#1A1F71",
  mastercard: "#EB001B",
};

const STATUS_COLORS: Record<string, string> = {
  succeeded: "#10b981",
  cancelled: "#94a3b8",
  pending: "#f59e0b",
  failed: "#ef4444",
  processing: "#3b82f6",
  timeout: "#a855f7",
};

export function MerchantOverviewPage() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState<MerchantOverview | null>(null);
  const [recent, setRecent] = useState<Payment[]>([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([getMerchantOverview(days), listPayments({ limit: 6 })])
      .then(([ov, p]) => {
        setOverview(ov);
        setRecent(p.data);
      })
      .finally(() => setLoading(false));
  }, [days]);

  if (loading || !overview) return <div className="card card-body text-[#7e8299]">Chargement…</div>;

  const methods = Object.entries(overview.by_method).sort((a, b) => b[1].count - a[1].count);
  const totalMethodCount = methods.reduce((acc, [, v]) => acc + v.count, 0);

  // Agrégation multi-currency des balances (tous wallets confondus)
  const walletsByCurrency = new Map<string, number>();
  for (const w of overview.wallets) {
    walletsByCurrency.set(w.currency, (walletsByCurrency.get(w.currency) ?? 0) + w.balance_minor);
  }
  const walletVolumes = Array.from(walletsByCurrency, ([currency, amount_minor]) => ({
    currency,
    amount_minor,
  })).sort((a, b) => a.currency.localeCompare(b.currency));

  // Data pour donut (répartition des paiements réussis par méthode)
  const methodPieData = methods.map(([method, stats]) => ({
    name: method,
    value: stats.count,
    fill: METHOD_COLORS[method] ?? "#64748b",
  }));

  // Data pour bar chart horizontal (funnel par statut)
  const statusOrder = ["succeeded", "processing", "pending", "cancelled", "failed", "timeout"];
  const statusBarData = Object.entries(overview.by_status)
    .sort((a, b) => statusOrder.indexOf(a[0]) - statusOrder.indexOf(b[0]))
    .map(([status, stats]) => ({
      status,
      count: stats.count,
      fill: STATUS_COLORS[status] ?? "#64748b",
    }));

  return (
    <div>
      <PageHeader
        title="Vue d'ensemble"
        breadcrumbs={[{ label: "Mon espace" }, { label: "Tableau de bord" }]}
        actions={
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="form-input py-2 px-3 text-sm w-auto"
          >
            <option value={7}>7 jours</option>
            <option value={30}>30 jours</option>
            <option value={90}>90 jours</option>
          </select>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <StatCard
          label="Volume aujourd'hui"
          value={formatVolumes(overview.today.volumes)}
          sub={`${overview.today.count} paiement${overview.today.count > 1 ? "s" : ""}`}
          icon={<Activity size={20} />}
          color="primary"
        />
        <StatCard
          label="Paiements réussis"
          value={String(overview.totals.succeeded_count)}
          sub={`${days} derniers jours`}
          icon={<CheckCircle2 size={20} />}
          color="success"
        />
        <StatCard
          label="Taux de conversion"
          value={`${overview.totals.conversion_rate.toFixed(1)}%`}
          sub={`sur ${overview.totals.count} tentatives`}
          icon={<TrendingUp size={20} />}
          color="info"
        />
      </div>

      {walletVolumes.length > 0 && (
        <div
          className="grid grid-cols-1 gap-4 mb-6"
          style={{ gridTemplateColumns: `repeat(${Math.min(walletVolumes.length, 4)}, minmax(0, 1fr))` }}
        >
          {walletVolumes.map((v) => {
            const walletsOfCurrency = overview.wallets.filter((w) => w.currency === v.currency);
            const reserved = walletsOfCurrency.reduce((sum, w) => sum + w.reserved_balance_minor, 0);
            return (
              <BalanceCard
                key={v.currency}
                currency={v.currency}
                availableMinor={v.amount_minor}
                reservedMinor={reserved}
                walletsCount={walletsOfCurrency.length}
                onClick={() => navigate("/wallets")}
              />
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        {/* Methods breakdown — donut */}
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Répartition par méthode</div>
              <div className="card-subtitle">Paiements réussis · {days} jours</div>
            </div>
          </div>
          <div className="card-body">
            {methodPieData.length === 0 ? (
              <div className="text-center text-[#7e8299] py-8 text-sm">Aucun paiement réussi sur la période</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
                <div style={{ width: "100%", height: 220 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={methodPieData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={55}
                        outerRadius={85}
                        paddingAngle={2}
                        stroke="none"
                      >
                        {methodPieData.map((entry) => (
                          <Cell key={entry.name} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value, _n, item) => {
                          const num = Number(value ?? 0);
                          const total = totalMethodCount || 1;
                          const pct = ((num / total) * 100).toFixed(1);
                          return [`${num} tx (${pct}%)`, (item as { payload?: { name?: string } })?.payload?.name ?? ""];
                        }}
                        contentStyle={{ borderRadius: 8, fontSize: 12 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2">
                  {methods.map(([method, stats]) => {
                    const pct = totalMethodCount > 0 ? (stats.count / totalMethodCount) * 100 : 0;
                    return (
                      <div key={method} className="flex items-center gap-2 text-sm">
                        <span
                          className="w-3 h-3 rounded-full shrink-0"
                          style={{ background: METHOD_COLORS[method] ?? "#64748b" }}
                        />
                        <span className="capitalize font-semibold flex-1">{method}</span>
                        <span className="text-[#7e8299] text-xs">
                          {stats.count} · {pct.toFixed(0)}%
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Status breakdown — horizontal bar chart */}
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Funnel par statut</div>
              <div className="card-subtitle">Toutes les tentatives · {days} jours</div>
            </div>
          </div>
          <div className="card-body">
            {statusBarData.length === 0 ? (
              <div className="text-center text-[#7e8299] py-8 text-sm">Aucune tentative</div>
            ) : (
              <>
                <div style={{ width: "100%", height: 200 }}>
                  <ResponsiveContainer>
                    <BarChart
                      data={statusBarData}
                      layout="vertical"
                      margin={{ top: 4, right: 16, left: 4, bottom: 4 }}
                    >
                      <XAxis type="number" allowDecimals={false} stroke="#94a3b8" fontSize={11} />
                      <YAxis
                        type="category"
                        dataKey="status"
                        stroke="#475569"
                        fontSize={12}
                        width={90}
                      />
                      <Tooltip
                        cursor={{ fill: "rgba(148,163,184,0.08)" }}
                        formatter={(value) => [`${Number(value ?? 0)} tx`, "Nombre"]}
                        contentStyle={{ borderRadius: 8, fontSize: 12 }}
                      />
                      <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                        {statusBarData.map((entry) => (
                          <Cell key={entry.status} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {Object.entries(overview.by_status)
                    .sort((a, b) => b[1].count - a[1].count)
                    .map(([status, stats]) => (
                      <div
                        key={status}
                        className="flex items-center justify-between p-2 rounded-lg bg-[#fcfcfd] border border-[#eff2f5]"
                      >
                        <Badge color={statusColor(status)}>{status}</Badge>
                        <div className="text-right">
                          <div className="font-bold text-[#181c32] text-sm leading-none">{stats.count}</div>
                          <div className="text-[10px] text-[#7e8299] mt-0.5">
                            {formatVolumes(stats.volumes)}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Recent payments */}
      <div className="card">
        <div className="card-header">
          <div>
            <div className="card-title">Paiements récents</div>
            <div className="card-subtitle">Les 6 dernières transactions</div>
          </div>
          <button onClick={() => navigate("/payments")} className="btn btn-light btn-sm">
            Voir tous
            <ArrowRight size={14} />
          </button>
        </div>
        {recent.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-[#7e8299]">Aucune transaction encore</div>
        ) : (
          <div className="divide-y divide-[#eff2f5]">
            {recent.map((p) => (
              <button
                key={p.id}
                onClick={() => navigate(`/payments/${p.id}`)}
                className="w-full px-6 py-4 hover:bg-[#fcfcfd] flex items-center gap-4 text-left transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-mono text-sm text-[#181c32]">{p.external_reference}</div>
                    <Badge color={statusColor(p.status)}>{p.status}</Badge>
                  </div>
                  <div className="text-xs text-[#7e8299] mt-0.5">
                    {p.payment_method} · {formatRelative(p.created_at)}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-bold text-[#181c32]">{formatAmount(p.amount_minor, p.currency)}</div>
                  <div className="text-xs text-[#7e8299]">
                    {[p.customer_first_name, p.customer_last_name].filter(Boolean).join(" ") || p.customer_phone || "—"}
                  </div>
                </div>
                <ArrowRight size={14} className="text-[#cbd5e0] shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  icon,
  color,
}: {
  label: string;
  value: string;
  sub?: string;
  icon: React.ReactNode;
  color: "primary" | "success" | "info" | "warning";
}) {
  const cls: Record<typeof color, string> = {
    primary: "bg-brand-50 text-brand-700",
    success: "bg-success-50 text-success-700",
    info: "bg-info-50 text-info-700",
    warning: "bg-warning-50 text-warning-700",
  };
  return (
    <div className="card card-body">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-[#7e8299] font-bold">{label}</div>
          <div className="text-2xl font-bold text-[#181c32] mt-2 truncate">{value}</div>
          {sub && <div className="text-xs text-[#7e8299] mt-1 truncate">{sub}</div>}
        </div>
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${cls[color]}`}>{icon}</div>
      </div>
    </div>
  );
}

function BalanceCard({
  currency,
  availableMinor,
  reservedMinor,
  walletsCount,
  onClick,
}: {
  currency: string;
  availableMinor: number;
  reservedMinor: number;
  walletsCount: number;
  onClick?: () => void;
}) {
  const gradient =
    currency === "USD"
      ? "from-emerald-500 to-emerald-700"
      : currency === "CDF"
      ? "from-sky-500 to-sky-700"
      : "from-slate-500 to-slate-700";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`card overflow-hidden relative bg-gradient-to-br ${gradient} text-left transition-transform hover:-translate-y-0.5 hover:shadow-lg cursor-pointer`}
    >
      <div className="px-6 py-5 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wallet size={16} className="opacity-90" />
            <div className="text-sm opacity-90">Solde {currency}</div>
          </div>
          <span className="text-[10px] font-bold tracking-wider bg-white/20 rounded px-2 py-0.5">
            {currency}
          </span>
        </div>
        <div className="text-3xl font-bold mt-2 truncate">{formatAmount(availableMinor, currency)}</div>
        <div className="text-xs opacity-80 mt-1">
          Réservé : {formatAmount(reservedMinor, currency)} · {walletsCount} wallet
          {walletsCount > 1 ? "s" : ""}
        </div>
        <div className="text-[10px] uppercase tracking-wider opacity-70 mt-2 flex items-center gap-1">
          Voir les opérations <ArrowRight size={10} />
        </div>
      </div>
    </button>
  );
}
